<script lang="ts" setup>
import projectStore from '@/store/ProjectStore.ts'
import formStore from '@/store/FormStore.ts'
import { StepsController } from '@/controllers/StepsController.ts'
import { CharacteristicType } from '@/helper/Types.ts'
import { Heatpump } from '@/services/models/implementations/Heatpump.ts'
import { GeneralProduct } from '@/services/models/implementations/GeneralProduct.ts'
import HeatPumpFilterBar from '@/components/steps/heat-pumps/HeatPumpFilterBar.vue'
import HeatPumpCard from '@/components/steps/heat-pumps/HeatPumpCard.vue'
import { computed, ref } from 'vue'
import SectionHeadline from '@/components/SectionHeadline.vue'
import ProductAvailabilityLink from '@/components/ProductAvailabilityLink.vue'
import userStore from '@/store/UserStore.ts'
import appStore from '@/store/AppStore.ts'

const searchText = ref<string>('')

const suggestedHeatPumps = computed((): Heatpump[] => {
  return projectStore.heatPumps as Heatpump[]
})

const showFilterBar = computed((): boolean => {
  return suggestedHeatPumps.value?.length >= 1 || !!searchText.value
})

const selectedHeatPump = computed((): Heatpump | undefined => {
  return suggestedHeatPumps.value.find(
    product =>
      product.id === formStore.data.heatPumpId && product.chr.cascadeSize === formStore.data.heatPumpCascadeSize
  )
})

function selectHeatPump(heatpump: Heatpump): void {
  if (isHeatPumpSelected(heatpump)) {
    return
  }

  formStore.data.heatPumpId = heatpump.id
  formStore.data.heatPumpCascadeSize = heatpump.chr.cascadeSize

  formStore.data.additionalProductId = ''
  formStore.data.applianceInterfaceId = ''

  const recommendedId = heatpump.chr.additionalProducts?.find(ap => ap.recommended)?.id
  setAdditionalProductId(recommendedId || heatpump.chr.additionalProducts?.[0].id || '')

  StepsController.currentStepValid()
}

function isHeatPumpSelected(heatpump: Heatpump): boolean {
  return heatpump.id === formStore.data.heatPumpId && heatpump.chr.cascadeSize === formStore.data.heatPumpCascadeSize
}

function setAdditionalProductId(id: string) {
  const additionalProduct = selectedHeatPump.value?.chr.additionalProducts?.find(product => product.id === id)
  let includeSaleSet = false

  if (additionalProduct?.salesPackageType || selectedHeatPump.value?.salesPackageType) {
    includeSaleSet = true
    formStore.data.salePackage = additionalProduct?.salesPackageType ? additionalProduct.salesPackageType : selectedHeatPump.value?.salesPackageType
  } else if (additionalProduct || !selectedHeatPump.value?.salesPackageType) {
    includeSaleSet = false
    formStore.data.salePackage = ''
    formStore.data.salePackageId = ''
  }

  const applianceInterface =
    new GeneralProduct(additionalProduct).getProductCharacteristic(CharacteristicType.APPLIANCE_INTERFACE) || undefined

  appStore.setStepCondition('includeSaleSet', includeSaleSet)

  formStore.data.applianceInterfaceId = applianceInterface ? id : ''
  formStore.data.additionalProductId = applianceInterface ? '' : id
}

function selectAdditionalProduct(param: string, heatPump: Heatpump) {
  selectHeatPump(heatPump)
  setAdditionalProductId(param)
  StepsController.currentStepValid()
}

function validate(): boolean {
  return (
    !!formStore.data.heatPumpId &&
    (!selectedHeatPump.value?.chr.additionalProducts?.length || !!formStore.data.additionalProductId || !!formStore.data.applianceInterfaceId)
  )
}

defineExpose({
  validate
})
</script>

<template>
  <div class="steps__content" data-hj-whitelist>
    <section>
      <HeatPumpFilterBar v-if="showFilterBar" />

      <div class="steps-table__header">
        <SectionHeadline translation-key="step_heat_pumps.section_title" />
        <ProductAvailabilityLink :availability-link="userStore.productAvailabilityLinks?.heatPumps" />
      </div>

      <form id="heat-pump-form" class="steps__form" @submit.prevent="StepsController.nextClicked()">
        <HeatPumpCard
          v-for="heatPump in suggestedHeatPumps"
          :key="`${heatPump.id}.${heatPump.chr.cascadeSize}`"
          :heat-pump="heatPump"
          :is-selected="isHeatPumpSelected(heatPump)"
          @on-select-heat-pump="selectHeatPump(heatPump)"
          @select-additional-product="val => selectAdditionalProduct(val, heatPump)"
        />
        <input class="is--invisible" type="submit">
      </form>
      <span v-if="!suggestedHeatPumps.length" class="steps__info">
        {{ $t('global.no_components_available_for_selection') }}
      </span>
    </section>
  </div>
</template>
